import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../../services/analytics/posthogService';
import { bookDemo, getPrefill, prefetchAvailabilityForMonth, submitDemoLead } from '../../../services/demoService';
import { startOfPeriod } from '../../../utils/dateUtils';
import DemoLayout from './components/DemoLayout';
import LocationsStep from './steps/LocationsStep';
import BusinessNameStep from './steps/BusinessNameStep';
import RevenueStep from './steps/RevenueStep';
import BelowThresholdStep from './steps/BelowThresholdStep';
import ChallengesStep from './steps/ChallengesStep';
import ReviewStep from './steps/ReviewStep';
import ContactStep from './steps/ContactStep';
import ScheduleStep, { BookOutcome } from './steps/ScheduleStep';
import SuccessStep from './steps/SuccessStep';
import { BELOW_THRESHOLD_RANGE } from './bookDemoConstants';
import { BookDemoFormData, BookDemoStep, INITIAL_FORM_DATA, STEP_ORDER } from './types';

const BookDemoPage: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') ?? undefined;

  const [step, setStep] = useState<BookDemoStep>('name');
  const [formData, setFormData] = useState<BookDemoFormData>(INITIAL_FORM_DATA);
  const [bookedStartTime, setBookedStartTime] = useState<string | null>(null);
  const hadPrefill = useRef(false);
  const lastViewedStep = useRef<BookDemoStep | null>(null);
  const seenSteps = useRef<Set<BookDemoStep>>(new Set());

  useEffect(() => {
    trackEvent('demo_flow_started', { had_ref: !!ref });
  }, [ref]);

  /**
   * One view event per step transition — the ref guard keeps re-renders (and StrictMode's
   * double-invoke) from counting twice, while stepping back to an earlier screen deliberately
   * fires again, marked `revisit`. The two off-flow screens are not in STEP_ORDER, so they
   * report step_index -1.
   */
  useEffect(() => {
    if (lastViewedStep.current === step) return;
    lastViewedStep.current = step;

    const revisit = seenSteps.current.has(step);
    seenSteps.current.add(step);

    trackEvent('demo_flow_step_viewed', {
      step: step === 'belowThreshold' ? 'below_threshold_stop' : step,
      step_index: STEP_ORDER.indexOf(step),
      revisit,
    });
  }, [step]);

  // Prefilled links only pre-populate the answers — the visitor still walks every step.
  useEffect(() => {
    if (!ref) return;
    let cancelled = false;

    getPrefill(ref)
      .then(prefill => {
        if (cancelled || !prefill) return;
        hadPrefill.current = true;
        setFormData(prev => ({
          ...prev,
          businessName: prefill.businessName ?? prev.businessName,
          restaurantCount: prefill.restaurantCount20Plus ? null : prefill.restaurantCount ?? prev.restaurantCount,
          restaurantCount20Plus: prefill.restaurantCount20Plus,
          revenueRange: prefill.revenueRange ?? prev.revenueRange,
          contactName: prefill.contactName ?? prev.contactName,
          email: prefill.email ?? prev.email,
          phone: prefill.phone ?? prev.phone,
        }));
      })
      .catch(error => console.error('Failed to load demo prefill', error));

    return () => {
      cancelled = true;
    };
  }, [ref]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  /**
   * Warm the calendar while they fill in their contact details, so the schedule step usually
   * paints its slots immediately. Same month the schedule step opens on; if it has not landed
   * by the time they get there, the step just falls back to its normal loading state.
   */
  useEffect(() => {
    if (step !== 'contact') return;
    prefetchAvailabilityForMonth(startOfPeriod(new Date(), 'month'));
  }, [step]);

  const patchForm = useCallback(
    (patch: Partial<BookDemoFormData>) => setFormData(prev => ({ ...prev, ...patch })),
    []
  );

  const goToStep = (next: BookDemoStep) => setStep(next);

  const completeStep = (current: BookDemoStep, next: BookDemoStep) => {
    trackEvent('demo_flow_step_completed', { step: current });
    goToStep(next);
  };

  const goBackFrom = (current: BookDemoStep) => {
    const index = STEP_ORDER.indexOf(current);
    if (index <= 0) {
      navigate('/');
      return;
    }
    goToStep(STEP_ORDER[index - 1]);
  };

  const handleRevenueContinue = () => {
    if (formData.revenueRange === BELOW_THRESHOLD_RANGE) {
      trackEvent('demo_flow_below_threshold', {});
      goToStep('belowThreshold');
      return;
    }
    completeStep('revenue', 'challenges');
  };

  const handleLeadSubmit = async (): Promise<boolean> => {
    try {
      await submitDemoLead({
        email: formData.email.trim(),
        businessName: formData.businessName.trim() || undefined,
        restaurantCount: formData.restaurantCount,
        restaurantCount20Plus: formData.restaurantCount20Plus,
        locale: i18n.language,
        ref,
        website: formData.website,
      });
      return true;
    } catch (error) {
      console.error('Failed to submit below-threshold demo lead', error);
      return false;
    }
  };

  const handleBook = async (startTime: string): Promise<BookOutcome> => {
    if (!formData.revenueRange) return 'error';

    try {
      const result = await bookDemo({
        businessName: formData.businessName.trim(),
        restaurantCount: formData.restaurantCount,
        restaurantCount20Plus: formData.restaurantCount20Plus,
        revenueRange: formData.revenueRange,
        challenges: formData.challenges,
        contactName: formData.contactName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        locale: i18n.language,
        startTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ref,
        website: formData.website,
      });

      if (!result.booked) return 'slot_taken';

      // Booking is how the schedule step completes, so it reports like every other advance.
      trackEvent('demo_flow_step_completed', { step: 'schedule' });
      trackEvent('demo_request_submitted', {
        count: formData.restaurantCount20Plus ? '20+' : formData.restaurantCount,
        revenue_range: formData.revenueRange,
        challenges_count: formData.challenges.length,
        had_prefill: hadPrefill.current,
      });
      setBookedStartTime(result.startTime);
      goToStep('success');
      return 'booked';
    } catch (error) {
      console.error('Failed to book demo', error);
      return 'error';
    }
  };

  const handleClose = () => navigate('/');

  if (step === 'success' && bookedStartTime) {
    return (
      <DemoLayout onClose={handleClose}>
        <SuccessStep startTime={bookedStartTime} email={formData.email.trim()} onReturn={handleClose} />
      </DemoLayout>
    );
  }

  // The soft stop replaces step 4 onwards, so the bar stays on the revenue step.
  const progressStep = step === 'belowThreshold' ? STEP_ORDER.indexOf('revenue') + 1 : STEP_ORDER.indexOf(step) + 1;

  return (
    <DemoLayout onClose={handleClose} currentStep={progressStep}>
      {step === 'locations' && (
        <LocationsStep
          count={formData.restaurantCount}
          twentyPlus={formData.restaurantCount20Plus}
          onChange={(restaurantCount, restaurantCount20Plus) =>
            patchForm({ restaurantCount, restaurantCount20Plus })
          }
          onBack={() => goBackFrom('locations')}
          onContinue={() => completeStep('locations', 'revenue')}
        />
      )}

      {step === 'name' && (
        <BusinessNameStep
          value={formData.businessName}
          onChange={businessName => patchForm({ businessName })}
          onBack={() => goBackFrom('name')}
          onContinue={() => completeStep('name', 'locations')}
        />
      )}

      {step === 'revenue' && (
        <RevenueStep
          value={formData.revenueRange}
          onChange={revenueRange => patchForm({ revenueRange })}
          onBack={() => goBackFrom('revenue')}
          onContinue={handleRevenueContinue}
        />
      )}

      {step === 'belowThreshold' && (
        <BelowThresholdStep
          email={formData.email}
          website={formData.website}
          onChange={patchForm}
          onSubmit={handleLeadSubmit}
          onBack={() => goToStep('revenue')}
          onClose={handleClose}
        />
      )}

      {step === 'challenges' && (
        <ChallengesStep
          value={formData.challenges}
          onChange={challenges => patchForm({ challenges })}
          onBack={() => goBackFrom('challenges')}
          onContinue={() => completeStep('challenges', 'review')}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          data={formData}
          onBack={() => goBackFrom('review')}
          onContinue={() => completeStep('review', 'contact')}
        />
      )}

      {step === 'contact' && (
        <ContactStep
          data={formData}
          onChange={patchForm}
          onBack={() => goBackFrom('contact')}
          onContinue={() => completeStep('contact', 'schedule')}
        />
      )}

      {step === 'schedule' && <ScheduleStep onBook={handleBook} onBack={() => goBackFrom('schedule')} />}
    </DemoLayout>
  );
};

export default BookDemoPage;
